/**
 * Pure factory that returns a fully-formed ConversationAction object for the
 * FOMO ("teammates using Rovo") nudge.
 *
 * Why a factory (not a hook) at this layer?
 *   - Confluence consumes nudges as objects inside a `NudgeEntry[]`
 *     (declarative, via `useViewPageNudgeConfig`).
 *   - Jira consumes nudges by calling
 *     `updateConfiguration({ nudges: [...] })` imperatively
 *     (via `useAiRovoCurrentRouteConfigurationStoreActions`).
 *   Both surfaces can call a pure builder; only the surrounding glue differs.
 *
 * Eligibility (experiment treatment, non-Rovo-MAU TAP trait, "show once"
 * guard, launch-date scheduling via Statsig) lives in the product hooks /
 * Statsig config. This builder assumes "we've decided to show the nudge".
 */

import React from 'react';

import { HandleActionType, type ChatAction } from '@atlassian/rovo-conversation-actions/types';

import { FOMO_NUDGE_ID, FOMO_STATIC_PROMPT } from '../constants';
import type { FomoNudgeContext } from '../types';
import { FomoNudgeIcon } from '../ui/FomoNudgeIcon';

/**
 * Pure factory that returns a fully-formed ChatAction object for the
 * FOMO ("teammates using Rovo") nudge.
 *
 * i18n (Option A): title and description are passed in as pre-formatted
 * strings from the product hook (Confluence via react-intl, Jira via
 * @atlassian/jira-intl). Use messages.fomoNudgeTitle and
 * messages.fomoNudgeDescription from `../i18n/messages` at the call site.
 *
 * Eligibility (experiment treatment, non-Rovo-MAU TAP trait, "show once"
 * guard, launch-date scheduling via Statsig) lives in the product hooks /
 * Statsig config. This builder assumes "we've decided to show the nudge".
 */
export function createFomoNudgeAction(ctx: FomoNudgeContext): ChatAction {
	return {
		id: FOMO_NUDGE_ID,
		title: ctx.title,
		description: ctx.description,
		icon: () => <FomoNudgeIcon collaborators={ctx.collaborators} />,
		handleType: HandleActionType.Chat,
		type: 'convo-starter-action',
		staticPrompt: FOMO_STATIC_PROMPT,
		starterType: 'static',
		secondaryTitle: ctx.description,
		// onClick intentionally omitted for now: triggerAction early-returns when
		// onClick is defined, preventing HandleActionType.Chat from opening chat.
		// TODO(Phase 5): set onClick only for storage/analytics side-effects once
		// triggerAction's early-return behaviour is addressed upstream.
		nudgeContext: { id: FOMO_NUDGE_ID, selfEligible: true },
	};
}
